import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Link,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import productService from '../../services/productService';
import categoryService from '../../services/categoryService';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import { getTaxDropdownLabelWithRate } from '../../utils/taxDisplay';

// Column widths (px) - Qty, MRP, prices wider; UOM narrow (abbreviation only)
const COL_WIDTHS = {
  name: 200,
  hsnCode: 90,
  categoryId: 160,
  quantity: 95,
  unitId: 65,
  taxId: 120,
  mrp: 95,
  purchasePrice: 105,
  sellingPrice: 105,
  sellingPriceWithTax: 95,
  wholesalePrice: 105,
  wholesalePriceWithTax: 95,
};

const INITIAL_PRODUCT_BATCH = 150;
const PRODUCT_PAGE_SIZE = 500;
const ROW_HEIGHT_PX = 36;
const VIRTUAL_OVERSCAN = 6;
const LOOKUPS_CACHE_KEY = 'quickEntry_lookups_v1';

const nativeSelectSx = {
  width: '100%',
  border: 'none',
  background: 'transparent',
  fontSize: '0.8125rem',
  fontFamily: 'inherit',
  padding: '2px 0',
  outline: 'none',
  cursor: 'pointer',
};

const emptyRowObj = () => ({
  id: null,
  sku: '',
  name: '',
  hsnCode: '',
  categoryId: '',
  quantity: 0,
  taxId: '',
  mrp: '',
  purchasePrice: '',
  sellingPrice: '',
  sellingPriceWithTax: '',
  wholesalePrice: '',
  wholesalePriceWithTax: '',
  unitId: '',
  status: 'ACTIVE',
});

const mapProductToRow = (p, quantityOverride) => {
  const taxId = p.tax?.id ?? p.taxId ?? '';
  const rate = p.tax?.rate != null ? Number(p.tax.rate) : 0;
  const factor = 1 + rate / 100;
  const sp = parseFloat(p.sellingPrice);
  const wp = parseFloat(p.wholesalePrice);
  const qty =
    quantityOverride !== undefined ? quantityOverride : p.quantity ?? 0;

  return {
    id: p.id,
    sku: p.sku || '',
    name: p.name || '',
    hsnCode: p.hsnCode || '',
    categoryId: p.category?.id ?? p.categoryId ?? '',
    quantity: qty,
    taxId,
    mrp: p.mrp ?? '',
    purchasePrice: p.purchasePrice ?? '',
    sellingPrice: p.sellingPrice ?? '',
    sellingPriceWithTax: !Number.isNaN(sp) && sp > 0 ? (sp * factor).toFixed(2) : '',
    wholesalePrice: p.wholesalePrice ?? '',
    wholesalePriceWithTax: !Number.isNaN(wp) && wp > 0 ? (wp * factor).toFixed(2) : '',
    unitId: p.unit?.id ?? p.unitId ?? '',
    status: p.status || 'ACTIVE',
  };
};

const mapLocationProductToRow = (lp) => {
  const rate = lp.taxRate != null ? Number(lp.taxRate) : 0;
  const factor = 1 + rate / 100;
  const sp = Number(lp.sellingPrice) || Number(lp.purchasePrice) || 0;
  const wp = Number(lp.wholesalePrice) || 0;
  return {
    id: lp.productId,
    sku: lp.productCode || '',
    name: lp.productName || '',
    hsnCode: lp.hsnCode || '',
    categoryId: lp.categoryId ?? '',
    quantity: Number(lp.quantity) || 0,
    taxId: lp.taxId ?? '',
    mrp: lp.mrp ?? '',
    purchasePrice: lp.purchasePrice ?? '',
    sellingPrice: sp,
    sellingPriceWithTax: sp > 0 ? (sp * factor).toFixed(2) : '',
    wholesalePrice: lp.wholesalePrice ?? '',
    wholesalePriceWithTax: wp > 0 ? (wp * factor).toFixed(2) : '',
    unitId: lp.unitId ?? '',
    status: lp.status || 'ACTIVE',
  };
};

async function fetchAllProductsPaginated() {
  const first = await productService.getAllProducts(0, PRODUCT_PAGE_SIZE, 'name', 'asc');
  const all = [...(first.content || [])];
  const totalPages = first.totalPages || 1;
  if (totalPages <= 1) return all;

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      productService.getAllProducts(i + 1, PRODUCT_PAGE_SIZE, 'name', 'asc')
    )
  );
  rest.forEach((page) => all.push(...(page.content || [])));
  return all;
}

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: '#2c3e50',
  color: '#fff',
  border: '1px solid #34495e',
  padding: '10px 12px',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

// Kept for compatibility; Product Name column no longer sticky so all columns scroll together
const stickyProductNameCellSx = (bgcolor) => ({ backgroundColor: bgcolor || '#fff' });

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #bdc3c7',
  padding: '6px 10px',
  backgroundColor: theme.palette.background.paper,
  verticalAlign: 'middle',
  '& .MuiInput-root': { backgroundColor: 'transparent' },
  '& .MuiInput-input': { padding: 0 },
}));

/**
 * Quick Product Entry - Excel-style grid for add/update products.
 * Uses same createProduct/updateProduct APIs as Products page.
 * No delete - use existing Products page to make inactive.
 * New products are created as ACTIVE; use Full Product Form to change status.
 */
const QuickProductEntryPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const user = getCurrentUser();
  const companyId = user?.companyId || user?.company_id || 1;

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [lookupsReady, setLookupsReady] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirtyRowIds, setDirtyRowIds] = useState(new Set());
  const cellRefs = useRef({});
  const tableContainerRef = useRef(null);
  const [focusedCell, setFocusedCell] = useState({ rowIdx: null, colIdx: null });
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(520);

  const COLUMN_KEYS = ['name', 'hsnCode', 'categoryId', 'quantity', 'unitId', 'taxId', 'mrp', 'purchasePrice', 'sellingPrice', 'sellingPriceWithTax', 'wholesalePrice', 'wholesalePriceWithTax'];
  const COL_COUNT = COLUMN_KEYS.length;

  const noSpinnerSx = {
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  };

  // Prevent mouse wheel from changing numeric values when scrolling table
  const handleNumberWheel = (e) => {
    if (e.target?.type === 'number') {
      e.preventDefault();
    }
  };

  // No extra white box: content displays directly in the column
  const cellFieldSx = {
    '& .MuiInput-root': { backgroundColor: 'transparent' },
    '& .MuiInput-input': { padding: 0 },
  };
  // Cursor identification: highlight focused cell like POS (light blue + outline)
  const getCellStyle = useCallback((rowIdx, colIdx, row) => {
    const isFocused = focusedCell?.rowIdx === rowIdx && focusedCell?.colIdx === colIdx;
    const rowBg = !row.id ? '#e8f5e9' : dirtyRowIds.has(row.id) ? '#fff8e1' : (rowIdx % 2 === 0 ? '#ffffff' : '#f0f4f8');
    return {
      minWidth: COL_WIDTHS[COLUMN_KEYS[colIdx]],
      backgroundColor: isFocused ? '#e3f2fd' : rowBg,
      outline: isFocused ? '2px solid #1976d2' : 'none',
      outlineOffset: -2,
      zIndex: isFocused ? 1 : 0,
      position: 'relative',
    };
  }, [focusedCell, dirtyRowIds]);

  const applyLookups = useCallback((catRes, unitsRes, taxesData) => {
    const cats = catRes?.content || catRes || [];
    const unts = unitsRes?.content || unitsRes || [];
    const txs = Array.isArray(taxesData) ? taxesData : taxesData?.content || [];
    setCategories(cats);
    setUnits(unts);
    setTaxes(txs);
    setLookupsReady(true);
    try {
      sessionStorage.setItem(
        LOOKUPS_CACHE_KEY,
        JSON.stringify({ categories: cats, units: unts, taxes: txs, ts: Date.now() })
      );
    } catch {
      /* ignore quota */
    }
  }, []);

  const fetchLookups = useCallback(async () => {
    const [catRes, unitsRes, taxesData] = await Promise.all([
      categoryService.getAllCategories({ page: 0, size: 500 }),
      unitService.getAllUnits({ page: 0, size: 100 }),
      productService.getTaxes(),
    ]);
    applyLookups(catRes, unitsRes, taxesData);
  }, [applyLookups]);

  const fetchProducts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setProductsLoading(true);
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const hasLocation = storedUser?.location?.id != null;

      if (hasLocation) {
        const locationProducts = await productService.getLocationProducts();
        const list = Array.isArray(locationProducts) ? locationProducts : [];
        setRows([...list.map(mapLocationProductToRow), emptyRowObj()]);
      } else {
        const first = await productService.getAllProducts(0, INITIAL_PRODUCT_BATCH, 'name', 'asc');
        const firstList = first?.content || first || [];
        setRows([...firstList.map((p) => mapProductToRow(p)), emptyRowObj()]);

        const totalPages = first?.totalPages || 1;
        if (totalPages > 1) {
          setLoadingMoreProducts(true);
          fetchAllProductsPaginated()
            .then((all) => setRows([...all.map((p) => mapProductToRow(p)), emptyRowObj()]))
            .catch((e) => console.warn('Background product load failed', e))
            .finally(() => setLoadingMoreProducts(false));
        }
      }
      setDirtyRowIds(new Set());
    } catch (e) {
      console.error('Failed to load products', e);
      enqueueSnackbar('Failed to load products', { variant: 'error' });
      setRows([emptyRowObj()]);
    } finally {
      if (!silent) setProductsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    let cancelled = false;

    const hydrateFromCache = () => {
      try {
        const raw = sessionStorage.getItem(LOOKUPS_CACHE_KEY);
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        if (!parsed?.categories) return false;
        setCategories(parsed.categories);
        setUnits(parsed.units || []);
        setTaxes(parsed.taxes || []);
        setLookupsReady(true);
        return true;
      } catch {
        return false;
      }
    };

    (async () => {
      hydrateFromCache();
      setProductsLoading(true);
      try {
        await Promise.all([fetchLookups(), fetchProducts({ silent: true })]);
      } catch (e) {
        if (!cancelled) {
          console.error('Initial load failed', e);
          enqueueSnackbar('Failed to load page data', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchLookups, fetchProducts, enqueueSnackbar]);

  const getTaxRate = (taxId) => {
    if (!taxId) return 0;
    const t = taxes.find((x) => x.id != null && String(x.id) === String(taxId));
    return t ? (parseFloat(t.rate) || 0) : 0;
    // Normalize to number for id fields so Select value matches MenuItem value
  };

  const handleDropdownChange = (rowIndex, field, value) => {
    const normalized =
      (field === 'categoryId' || field === 'unitId' || field === 'taxId') && value !== '' && value != null
        ? Number(value)
        : value;
    handleCellChange(rowIndex, field, normalized);
  };

  const handleCellChange = (rowIndex, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const row = next[rowIndex];
      const newTaxId = field === 'taxId' ? value : row.taxId;
      const rate = getTaxRate(newTaxId);
      const factor = 1 + rate / 100;
      let updates = { [field]: value };
      if (field === 'sellingPriceWithTax') {
        const withTax = parseFloat(value);
        updates.sellingPrice = !isNaN(withTax) && withTax > 0 ? (withTax / factor).toFixed(2) : '';
      } else if (field === 'sellingPrice') {
        const withoutTax = parseFloat(value);
        updates.sellingPriceWithTax = !isNaN(withoutTax) && withoutTax > 0 ? (withoutTax * factor).toFixed(2) : '';
      } else if (field === 'wholesalePriceWithTax') {
        const withTax = parseFloat(value);
        updates.wholesalePrice = !isNaN(withTax) && withTax > 0 ? (withTax / factor).toFixed(2) : '';
      } else if (field === 'wholesalePrice') {
        const withoutTax = parseFloat(value);
        updates.wholesalePriceWithTax = !isNaN(withoutTax) && withoutTax > 0 ? (withoutTax * factor).toFixed(2) : '';
      } else if (field === 'taxId') {
        const sp = parseFloat(row.sellingPrice);
        const wp = parseFloat(row.wholesalePrice);
        updates.sellingPriceWithTax = !isNaN(sp) && sp > 0 ? (sp * factor).toFixed(2) : '';
        updates.wholesalePriceWithTax = !isNaN(wp) && wp > 0 ? (wp * factor).toFixed(2) : '';
      }
      next[rowIndex] = { ...row, ...updates };
      return next;
    });
    const row = rows[rowIndex];
    if (row?.id) {
      setDirtyRowIds((prev) => new Set(prev).add(row.id));
    } else {
      setDirtyRowIds((prev) => new Set(prev).add(`new-${rowIndex}`));
    }
  };

  const addNewRow = useCallback(() => {
    setRows((prev) => {
      const next = [...prev, emptyRowObj()];
      setDirtyRowIds((p) => new Set(p).add(`new-${prev.length}`));
      return next;
    });
  }, []);

  const focusCell = useCallback((rowIdx, colIdx) => {
    setTimeout(() => {
      const ref = cellRefs.current[`${rowIdx}-${colIdx}`];
      if (ref) {
        const el =
          ref.querySelector?.('input') ||
          ref.querySelector?.('select') ||
          ref.querySelector?.('[role="button"]') ||
          ref.querySelector?.('button');
        if (el?.focus) el.focus();
      }
    }, 0);
  }, []);

  const handleCellKeyDown = useCallback((rowIdx, colIdx, e) => {
    const row = rows[rowIdx] || {};
    const productName = String(row.name || '').trim();

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (e.target?.tagName === 'SELECT') return;
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        const lastIndex = rows.length - 1;
        if (lastIndex >= 0) focusCell(lastIndex, colIdx);
      }
      return;
    }

    if (e.key === 'Enter') {
      if (!productName && colIdx === 0) {
        e.preventDefault();
        enqueueSnackbar('Product name is required before moving to next field.', { variant: 'warning' });
        return;
      }
      e.preventDefault();
      if (colIdx < COL_COUNT - 1) {
        focusCell(rowIdx, colIdx + 1);
      } else if (!productName) {
        enqueueSnackbar('Enter Product name before adding a new row.', { variant: 'warning' });
      } else {
        const lastIndex = rows.length - 1;
        const lastHasName = String(rows[lastIndex]?.name || '').trim() !== '';
        if (lastHasName) {
          addNewRow();
          setTimeout(() => focusCell(rows.length, 0), 80);
        } else if (lastIndex >= 0) {
          setTimeout(() => focusCell(lastIndex, 0), 80);
        }
      }
    }
  }, [addNewRow, enqueueSnackbar, focusCell, rows]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setViewportHeight(el.clientHeight || 520));
    ro.observe(el);
    return () => ro.disconnect();
  }, [lookupsReady]);

  const virtualRange = useMemo(() => {
    const visible = Math.ceil(viewportHeight / ROW_HEIGHT_PX) + VIRTUAL_OVERSCAN;
    const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT_PX) - Math.floor(VIRTUAL_OVERSCAN / 2));
    const end = Math.min(rows.length, start + visible);
    return { start, end, topPad: start * ROW_HEIGHT_PX, bottomPad: Math.max(0, (rows.length - end) * ROW_HEIGHT_PX) };
  }, [rows.length, scrollTop, viewportHeight]);

  const categoryOptionsHtml = useMemo(
    () =>
      categories.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      )),
    [categories]
  );

  const unitOptionsHtml = useMemo(
    () =>
      units.map((u) => (
        <option key={u.id} value={u.id}>
          {u.abbreviation || u.name}
        </option>
      )),
    [units]
  );

  const taxOptionsHtml = useMemo(
    () =>
      taxes.map((t) => (
        <option key={t.id} value={t.id}>
          {getTaxDropdownLabelWithRate(t)}
        </option>
      )),
    [taxes]
  );

  // Focus empty row when ready
  useEffect(() => {
    if (!productsLoading && rows.length > 0) {
      const emptyRowIndex = rows.length - 1;
      const t = setTimeout(() => focusCell(emptyRowIndex, 0), 100);
      return () => clearTimeout(t);
    }
  }, [productsLoading, rows.length, focusCell]);

  const buildPayload = (row) => {
    const hsnVal = String(row.hsnCode || '').trim();
    return {
      name: String(row.name || '').trim(),
      hsnCode: hsnVal || null,
      categoryId: row.categoryId ? parseInt(row.categoryId) : null,
      unitId: row.unitId ? parseInt(row.unitId) : null,
      taxId: row.taxId ? parseInt(row.taxId) : null,
      quantity: parseFloat(row.quantity) || 0,
      mrp: row.mrp ? parseFloat(row.mrp) : null,
      purchasePrice: parseFloat(row.purchasePrice) || 0,
      sellingPrice: parseFloat(row.sellingPrice) || 0,
      wholesalePrice: row.wholesalePrice ? parseFloat(row.wholesalePrice) : null,
      minSalePrice: null,
      reorderLevel: null,
      status: row.status || 'ACTIVE',
      companyId: parseInt(companyId),
      manageable: true,
      sku: row.id ? (row.sku || undefined) : null,
      subUnit1Id: null,
      subUnit2Id: null,
    };
  };

  const handleSave = useCallback(async () => {
    const toCreate = rows.filter((r) => !r.id && (r.name || '').trim());
    const toUpdate = rows.filter((r) => r.id && dirtyRowIds.has(r.id));

    if (toCreate.length === 0 && toUpdate.length === 0) {
      enqueueSnackbar('No changes to save', { variant: 'info' });
      return;
    }

    // Validate mandatory fields: Product name, Category, Retailer Rate, UOM
    const invalidRows = [];
    const checkMandatory = (row) => {
      const name = String(row.name || '').trim();
      const hasCategory = !!row.categoryId;
      const hasUom = !!row.unitId;
      const sp = parseFloat(row.sellingPrice);
      const hasRetailerRate = !Number.isNaN(sp) && sp > 0;
      if (!name || !hasCategory || !hasUom || !hasRetailerRate) {
        invalidRows.push(row);
      }
    };
    toCreate.forEach(checkMandatory);
    toUpdate.forEach(checkMandatory);

    if (invalidRows.length > 0) {
      enqueueSnackbar('Product name, Category, Retailer Rate and UOM are required for all rows before saving.', {
        variant: 'warning',
      });
      return;
    }

    setSaving(true);
    let created = 0;
    let updated = 0;
    try {
      for (const row of toCreate) {
        const payload = buildPayload(row);
        await productService.createProduct(payload);
        created++;
      }
      for (const row of toUpdate) {
        const payload = buildPayload(row);
        await productService.updateProduct(row.id, payload);
        updated++;
      }
      if (created > 0 || updated > 0) {
        enqueueSnackbar(
          `Saved: ${created} created, ${updated} updated`,
          { variant: 'success' }
        );
        setDirtyRowIds(new Set());
        fetchProducts();
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [rows, dirtyRowIds, enqueueSnackbar, fetchProducts]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleSave]);

  const totalMinWidth = COLUMN_KEYS.reduce((sum, k) => sum + (COL_WIDTHS[k] || 90), 0);
  const visibleRows = rows.slice(virtualRange.start, virtualRange.end);

  return (
    <Layout>
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Paper elevation={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '2px solid #2c3e50', flexShrink: 0 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                Excel Product Entry
              </Typography>
              {!productsLoading && rows.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  {rows.length - 1} products
                  {loadingMoreProducts ? ' · loading more…' : ''}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Link component={RouterLink} to="/inventory/products" underline="hover" sx={{ fontSize: 14 }}>
                Full Product Form
              </Link>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={
                  saving ||
                  productsLoading ||
                  (dirtyRowIds.size === 0 && !rows.some((r) => !r.id && (r.name || '').trim()))
                }
              >
                Save (Ctrl+S)
              </Button>
            </Box>
          </Box>

          {(productsLoading || loadingMoreProducts) && (
            <LinearProgress sx={{ flexShrink: 0 }} />
          )}

          <TableContainer
            ref={tableContainerRef}
            sx={{ flex: 1, overflow: 'auto' }}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
          >
            <Table stickyHeader size="small" sx={{ minWidth: totalMinWidth }}>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.name }}>Product Name *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.hsnCode }}>HSN</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.categoryId }}>Category *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.quantity }}>Qty</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.unitId }}>UOM *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.taxId }}>GST</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.mrp }}>MRP</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.purchasePrice }}>Purchase</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.sellingPrice }}>Retailer *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.sellingPriceWithTax }}>Ret. w/Tax</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.wholesalePrice }}>Wholesaler</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.wholesalePriceWithTax }}>Whole. w/Tax</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {virtualRange.topPad > 0 && (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ height: virtualRange.topPad, p: 0, border: 0 }} />
                  </TableRow>
                )}
                {productsLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                )}
                {visibleRows.map((row, vi) => {
                  const idx = virtualRange.start + vi;
                  return (
                  <TableRow
                    key={row.id || `new-${idx}`}
                    sx={{
                      height: ROW_HEIGHT_PX,
                      bgcolor: !row.id ? '#e8f5e9' : dirtyRowIds.has(row.id) ? '#fff8e1' : (idx % 2 === 0 ? '#ffffff' : '#f0f4f8'),
                      '&:hover': { bgcolor: !row.id ? '#c8e6c9' : 'rgba(25, 118, 210, 0.08)' },
                    }}
                  >
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-0`] = el; }} sx={getCellStyle(idx, 0, row)}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.name}
                        onChange={(e) => handleCellChange(idx, 'name', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 0 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 0, e)}
                        placeholder="Product name"
                        InputProps={{ disableUnderline: true }}
                        sx={cellFieldSx}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-1`] = el; }} sx={getCellStyle(idx, 1, row)}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.hsnCode}
                        onChange={(e) => handleCellChange(idx, 'hsnCode', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 1 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 1, e)}
                        placeholder=""
                        InputProps={{ disableUnderline: true }}
                        sx={cellFieldSx}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-2`] = el; }} sx={getCellStyle(idx, 2, row)}>
                      <select
                        style={nativeSelectSx}
                        value={row.categoryId === '' || row.categoryId == null ? '' : String(row.categoryId)}
                        disabled={!lookupsReady}
                        onChange={(e) => handleDropdownChange(idx, 'categoryId', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 2 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 2, e)}
                      >
                        <option value="">Select</option>
                        {categoryOptionsHtml}
                      </select>
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-3`] = el; }} sx={getCellStyle(idx, 3, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.quantity}
                        onChange={(e) => handleCellChange(idx, 'quantity', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 3 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 3, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-4`] = el; }} sx={getCellStyle(idx, 4, row)}>
                      <select
                        style={nativeSelectSx}
                        value={row.unitId === '' || row.unitId == null ? '' : String(row.unitId)}
                        disabled={!lookupsReady}
                        onChange={(e) => handleDropdownChange(idx, 'unitId', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 4 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 4, e)}
                      >
                        <option value="">-</option>
                        {unitOptionsHtml}
                      </select>
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-5`] = el; }} sx={getCellStyle(idx, 5, row)}>
                      <select
                        style={nativeSelectSx}
                        value={row.taxId === '' || row.taxId == null ? '' : String(row.taxId)}
                        disabled={!lookupsReady}
                        onChange={(e) => handleDropdownChange(idx, 'taxId', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 5 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 5, e)}
                      >
                        <option value="">None</option>
                        {taxOptionsHtml}
                      </select>
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-6`] = el; }} sx={getCellStyle(idx, 6, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.mrp}
                        onChange={(e) => handleCellChange(idx, 'mrp', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 6 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 6, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-7`] = el; }} sx={getCellStyle(idx, 7, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.purchasePrice}
                        onChange={(e) => handleCellChange(idx, 'purchasePrice', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 7 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 7, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-8`] = el; }} sx={getCellStyle(idx, 8, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.sellingPrice}
                        onChange={(e) => handleCellChange(idx, 'sellingPrice', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 8 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 8, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-9`] = el; }} sx={getCellStyle(idx, 9, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.sellingPriceWithTax}
                        onChange={(e) => handleCellChange(idx, 'sellingPriceWithTax', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 9 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 9, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="0"
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-10`] = el; }} sx={getCellStyle(idx, 10, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.wholesalePrice}
                        onChange={(e) => handleCellChange(idx, 'wholesalePrice', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 10 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 10, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-11`] = el; }} sx={getCellStyle(idx, 11, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.wholesalePriceWithTax}
                        onChange={(e) => handleCellChange(idx, 'wholesalePriceWithTax', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 11 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 11, e)}
                         onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="0"
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                  </TableRow>
                  );
                })}
                {virtualRange.bottomPad > 0 && (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ height: virtualRange.bottomPad, p: 0, border: 0 }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Layout>
  );
};

export default QuickProductEntryPage;
